import { supabase } from './supabaseClient';
import { fmtNum } from './fmt';
import { fetchLinearTickers, resolveBybitSymbol } from './bybit';

/* ══════════════════════════════════════════════════════════════════════════
   RIASZTÁSOK (2b) — kliensoldali segédek
   ─────────────────────────────────────────────────────────────────────────
   A kvóta, a típus- és a csatorna-jogosultság SZERVEROLDALON dől el
   (web3kurzus-landing migráció 20261006120000_alerts.sql, alerts_guard
   trigger). Az itteni ellenőrzés csak UX: előre szól, mielőtt a szerver
   elutasítana. A szerver hibakódjait (ALERT_*) a alertErrorMessage fordítja.
   Az alert-engine (Edge Function) értékel ki és küld — a böngésző nem.
   ══════════════════════════════════════════════════════════════════════════ */

export const LIMITS = { free: 3, plusz: 15, pro: 50 };
/* Szintenként (SZINTMATRIX, alerts_guard): FREE ár/tervszint, e-mail; Plusz
   + RSI és Telegram; PRO minden típus. `pro: true` = FREE-nek zárt típus,
   `plusz: true` = a Plusz már nyitja. */
export const typeAllowed = (level, type) => level === 'pro' || !TYPES[type]?.pro || (level === 'plusz' && !!TYPES[type]?.plusz);
export const presetAllowed = (level, p) => level === 'pro' || !p.pro || (level === 'plusz' && !!p.plusz);
export const telegramAllowed = (level) => level === 'pro' || level === 'plusz';
export const LEVEL_LABEL = { free: 'FREE', plusz: 'PLUSZ', pro: 'PRO' };

export const TYPES = {
  price_cross:     { label: 'Ár-keresztezés',  pro: false },
  plan_level:      { label: 'Terv-szint',      pro: false },
  rsi_zone:        { label: 'RSI-zóna',        pro: true, plusz: true },
  funding_extreme: { label: 'Funding-extrém',  pro: true },
  oi_change:       { label: 'OI-változás',     pro: true },
};

export const RSI_INTERVALS = [
  { value: '15', label: '15m' },
  { value: '60', label: '1h' },
  { value: '240', label: '4h' },
  { value: 'D', label: '1D' },
];
const TF_LABEL = Object.fromEntries(RSI_INTERVALS.map(i => [i.value, i.label]));

/* ── Bybit symbol + contract multiplier (1000PEPEUSDT = 1000 × PEPE) ── */
export function multiplierOf(symbol) {
  const m = /^(1000000|10000|1000)/.exec(symbol || '') || /(1000)USDT$/.exec(symbol || '');
  return m ? Number(m[1]) : 1;
}

/** CoinGecko coin → { symbol, mult, price } on Bybit, or null if there is no
 *  USDT perpetual. `price` is the Bybit price in coin units (÷ mult). */
export async function resolveCoin(coin) {
  const map = await fetchLinearTickers();
  const symbol = resolveBybitSymbol(coin?.symbol, coin?.current_price, map);
  if (!symbol) return null;
  const mult = multiplierOf(symbol);
  const price = parseFloat(map[symbol]?.lastPrice) / mult;
  return { symbol, mult, price: Number.isFinite(price) ? price : coin?.current_price ?? null };
}

/** Bybit symbol → { mult, price } (Funding / Derivatívák rows already know the symbol). */
export async function priceOf(symbol) {
  const map = await fetchLinearTickers();
  const mult = multiplierOf(symbol);
  const p = parseFloat(map[symbol]?.lastPrice) / mult;
  return { mult, price: Number.isFinite(p) ? p : null };
}

/** Which side of the level the price is on right now — stored at creation, so
 *  only a real crossing fires ("csak valódi keresztezésre szóljon"). */
export function initialState(direction, level, price) {
  if (!(price > 0)) return {};
  return { in: direction === 'up' ? price >= level : price <= level };
}

export const directionFor = (level, price) => (price > 0 && level < price ? 'down' : 'up');

/* ── Data ── */
const SELECT = 'id, type, symbol, params, channels, status, repeat, cooldown_minutes, plan_ref, label, created_at, last_triggered_at';

// Columns from the 2d migration (note, snooze). Until it is live the extended
// select fails with "column does not exist" — then we fall back to SELECT and
// stop asking for the new columns in this session.
const SELECT_2D = `${SELECT}, note, snoozed_until`;
let has2dColumns = true;

export async function listAlerts() {
  if (has2dColumns) {
    const { data, error } = await supabase.from('alerts').select(SELECT_2D).order('created_at', { ascending: false });
    if (!error) return data || [];
    if (!/note|snoozed_until|42703|PGRST204/i.test(`${error.code || ''} ${error.message || ''}`)) throw error;
    has2dColumns = false;
  }
  const { data, error } = await supabase.from('alerts').select(SELECT).order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

/* ── Mutes and presets (2d). Both tables may not exist yet: never throw. ── */

/** Active mutes ({ symbol, until }) of the signed-in user; [] on any error. */
export async function listMutes() {
  try {
    const { data, error } = await supabase.from('alert_mutes').select('symbol, until');
    if (error) return [];
    const now = Date.now();
    return (data || []).filter(m => !m.until || new Date(m.until).getTime() > now);
  } catch {
    return [];
  }
}

export async function deleteMute(userId, symbol) {
  const { error } = await supabase.from('alert_mutes').delete().eq('user_id', userId).eq('symbol', symbol);
  if (error) throw error;
}

/* Preset catalog — the names and descriptions mirror the Telegram bot. */
export const PRESET_CATALOG = {
  btc_move_5: { name: 'BTC ±5% mozgás', desc: 'két ár-riasztás: a mostani ár +5% és −5%', pro: false },
  eth_move_5: { name: 'ETH ±5% mozgás', desc: 'két ár-riasztás: a mostani ár +5% és −5%', pro: false },
  eth_rsi_4h: { name: 'ETH RSI-zóna 4ó', desc: '4 órás RSI 70 fölött és 30 alatt (ismétlődő)', pro: true, plusz: true },
  btc_rsi_1d: { name: 'BTC RSI-zóna napi', desc: 'napi RSI 70 fölött és 30 alatt (ismétlődő)', pro: true, plusz: true },
  btc_funding: { name: 'BTC funding-szélsőérték', desc: 'funding ±0,05% (ismétlődő)', pro: true },
};
const STATIC_PRESETS = Object.entries(PRESET_CATALOG).map(([id, p]) => ({ id, ...p }));

/** Active presets ordered by sort; the static catalog if the table is not readable. */
export async function listPresets() {
  try {
    const { data, error } = await supabase.from('alert_presets').select('id, pro, sort').eq('active', true).order('sort');
    if (error || !data?.length) return STATIC_PRESETS;
    // Only presets we can name in Hungarian are shown.
    return data.filter(r => PRESET_CATALOG[r.id]).map(r => ({ id: r.id, ...PRESET_CATALOG[r.id], pro: !!r.pro }));
  } catch {
    return STATIC_PRESETS;
  }
}

/** Creates the preset's alerts on the server; returns how many were created. */
export async function applyPreset(id) {
  const { data, error } = await supabase.rpc('alert_preset_apply', { p_preset: id });
  if (error) throw error;
  return Number(data) || 0;
}

export async function listEvents(limit = 50) {
  const { data, error } = await supabase.from('alert_events')
    .select('id, alert_id, triggered_at, observed_value, channels_sent, error, message')
    .order('triggered_at', { ascending: false }).limit(limit);
  if (error) throw error;
  return data || [];
}

export async function createAlerts(rows) {
  const { data, error } = await supabase.from('alerts').insert(rows).select(SELECT);
  if (error) throw error;
  return data || [];
}

export async function updateAlert(id, patch) {
  const { error } = await supabase.from('alerts').update(patch).eq('id', id);
  if (error) throw error;
}

export async function deleteAlert(id) {
  const { error } = await supabase.from('alerts').delete().eq('id', id);
  if (error) throw error;
}

export async function telegramStatus() {
  const { data, error } = await supabase.rpc('telegram_link_status');
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return { linked: !!row?.linked, linkedAt: row?.linked_at || null };
}

export async function telegramUnlink() {
  const { error } = await supabase.rpc('telegram_unlink');
  if (error) throw error;
}

const FN_URL = `${process.env.REACT_APP_SUPABASE_URL || 'https://ggeeqexormqnazwxzbsy.supabase.co'}/functions/v1/telegram-link`;

/** { url } on success, { error, code } otherwise. Never throws. */
export async function requestTelegramLink() {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return { error: 'A bejelentkezésed lejárt. Lépj be újra.', code: 'NO_SESSION' };
    const res = await fetch(FN_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
      body: '{}',
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || !json.url) return { error: json.error || 'Nem sikerült linket kérni. Próbáld újra.', code: json.code || `HTTP_${res.status}` };
    return { url: json.url };
  } catch (e) {
    return { error: 'Nem érhető el a szerver. Ellenőrizd a kapcsolatot.', code: 'NETWORK' };
  }
}

/* A típushiba szövege típusonként: az RSI-zóna a Pluszban is jár, a funding és az OI csak a PRO-ban. */
export const RSI_TYPE_TEXT = 'Az RSI-riasztás Plusszal vagy PRO-val érhető el.';

/** Server error (alerts_guard trigger) → code + Hungarian message. `type`: the alert type the error is about
 *  (the server code alone does not say it). */
export function alertErrorMessage(err, type = null) {
  const msg = String(err?.message || err || '');
  const code = (msg.match(/ALERT_[A-Z_]+|PRESET_ACTIVE|PRICE_UNAVAILABLE/) || [])[0] || null;
  const text = {
    PRESET_ACTIVE: 'Ez a csomag már aktív.',
    PRICE_UNAVAILABLE: 'Most nem érem el az árfolyamot, próbáld újra pár perc múlva.',
    ALERT_LIMIT: 'Elérted az aktív riasztások korlátját.',
    ALERT_TYPE_PRO: type === 'rsi_zone' ? RSI_TYPE_TEXT : 'Ez a riasztás-típus PRO-funkció.',
    ALERT_CHANNEL_PRO: 'A Telegram-értesítés Plusszal vagy PRO-val érhető el.',
    ALERT_PARAMS: 'Hiányzó vagy hibás érték. Ellenőrizd a szintet és az irányt.',
    ALERT_READONLY: 'Ez a módosítás nem lehetséges — töröld a riasztást, és állíts be újat.',
  }[code] || 'Nem sikerült menteni. Próbáld újra.';
  return { code, text };
}

/* ── Display ── */
// utils/fmt: tagolás négyjegyű számoknál is (4 183), tizedesvessző
const nf = (v, d) => fmtNum(v, d, 0);
export function fmtLevel(v) {
  const a = Math.abs(v);
  return nf(v, a >= 1000 ? 0 : a >= 100 ? 2 : a >= 1 ? 4 : a >= 0.01 ? 6 : 8);
}

export function describeAlert(a) {
  const p = a.params || {};
  if (a.type === 'price_cross' || a.type === 'plan_level') {
    const dir = p.direction === 'up' ? 'felfelé keresztezi' : 'lefelé keresztezi';
    const plan = a.type === 'plan_level' ? ` · „${p.plan_name}” – ${p.plan_label}` : '';
    return `${a.symbol} ${dir}: ${fmtLevel(p.level)} $${plan}`;
  }
  if (a.type === 'rsi_zone') return `${a.symbol} ${TF_LABEL[p.interval] || p.interval} RSI ${p.side === 'below' ? '<' : '>'} ${p.threshold} (lezárt gyertya)`;
  if (a.type === 'funding_extreme') return `${a.symbol} |funding| ≥ ${nf(p.threshold, 3)}%`;
  if (a.type === 'oi_change') return `${a.symbol} OI-változás ≥ ${nf(p.pct, 2)}% ${p.window_h} óra alatt`;
  return a.symbol;
}

/* ── Opening the panel from anywhere (coin header, Trade Tervező, tab rows) ── */
export const ALERT_DRAFT_EVENT = 'w3k:alert-draft';
/** draft: { kind: 'price' | 'plan' | 'rsi' | 'funding' | 'oi', source, … } */
export function openAlertDraft(draft) {
  window.dispatchEvent(new CustomEvent(ALERT_DRAFT_EVENT, { detail: draft || {} }));
}

/* ── "Not yet seen" triggered alerts (bell badge) — per device ── */
const seenKey = (uid) => `alerts_seen_${uid}`;
export function lastSeen(uid) {
  try { return localStorage.getItem(seenKey(uid)) || '1970-01-01T00:00:00Z'; } catch { return '1970-01-01T00:00:00Z'; }
}
export function markSeen(uid) {
  try { localStorage.setItem(seenKey(uid), new Date().toISOString()); } catch { /* private mode */ }
}
export async function unseenCount(uid) {
  const { count, error } = await supabase.from('alert_events')
    .select('id', { count: 'exact', head: true }).gt('triggered_at', lastSeen(uid));
  if (error) return 0;
  return count || 0;
}
